/**
 * Tracks the last route the user visited outside of "detail" pages (patient
 * page, etc.) so their Back buttons can return there directly. Plain history
 * back (-1) is not enough because tab switches inside those pages push
 * history entries.
 *
 * sessionStorage is used (per-tab) so the target survives a page refresh
 * while the user is on one of the excluded pages.
 */

const STORAGE_KEY = "appBackLocation";

/**
 * Base paths whose routes are never recorded as a back target. Add a page's
 * base path here when its Back button should use getBackLocation(); this
 * also keeps navigation between excluded pages (e.g. patient A -> patient B)
 * from overwriting the recorded origin.
 */
const EXCLUDED_BASE_PATHS = ["/patient"];

export const isExcludedFromBackNavigation = (pathname: string): boolean =>
  EXCLUDED_BASE_PATHS.some(
    (base) => pathname === base || pathname.startsWith(`${base}/`)
  );

export const recordBackLocation = (pathname: string, search = ""): void => {
  if (isExcludedFromBackNavigation(pathname)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, `${pathname}${search}`);
  } catch {
    // sessionStorage unavailable — callers fall back to a default route.
  }
};

export const getBackLocation = (): string | null => {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    return value?.startsWith("/") ? value : null;
  } catch {
    return null;
  }
};
